import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Download, Send, Share2, MessageCircle } from 'lucide-react';
import jsPDF from 'jspdf';
import FOSWatermark from '../components/FOSWatermark';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to format date/time in IST
const formatDateTimeIST = (dateString, type = 'date') => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    // Format options for IST
    const options = type === 'date' 
      ? { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'Asia/Kolkata' }
      : { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' };
    
    return date.toLocaleString('en-IN', options);
  } catch (e) {
    console.error('Date formatting error:', e);
    return 'N/A';
  }
};

const OrderDetail = ({ user }) => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSendEmailOpen, setIsSendEmailOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Logo state for receipts
  const [clientLogo, setClientLogo] = useState(null);
  
  // WhatsApp modal state
  const [isSendWhatsAppOpen, setIsSendWhatsAppOpen] = useState(false);
  const [whatsappData, setWhatsappData] = useState({ customerName: '', phoneNumber: '' });
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);

  // Fetch client logo on mount
  useEffect(() => {
    const fetchClientLogo = async () => {
      try {
        const token = localStorage.getItem('authToken') || localStorage.getItem('token');
        if (!token) return;
        const response = await axios.get(`${API}/onboarding/logo`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success && response.data.logo_url) {
          setClientLogo(`${BACKEND_URL}${response.data.logo_url}`);
        }
      } catch (error) {
        console.log('Logo not available');
      }
    };
    fetchClientLogo();
  }, []);

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
      });
      setOrder(response.data.order);
      
      // Parse order details JSON to get items
      const orderData = response.data.order;
      let orderItems = [];
      
      // Check multiple sources for items
      if (orderData.order_details && orderData.order_details.items) {
        // Already parsed object
        orderItems = orderData.order_details.items;
      } else if (orderData.order_details_json) {
        // JSON string - need to parse
        try {
          const parsed = typeof orderData.order_details_json === 'string' 
            ? JSON.parse(orderData.order_details_json) 
            : orderData.order_details_json;
          orderItems = parsed.items || [];
        } catch (parseErr) {
          console.error('[OrderDetail] Error parsing order_details_json:', parseErr);
        }
      } else if (response.data.items) {
        // Items at root level
        orderItems = response.data.items;
      }
      
      // Normalize item structure
      const normalizedItems = orderItems.map(item => ({
        name: item.name || item.item_name_snapshot || 'Unknown Item',
        price: item.price || item.item_price_snapshot || 0,
        quantity: item.quantity || 1,
        category: item.category || ''
      }));
      
      setItems(normalizedItems);
    } catch (error) {
      console.error('[OrderDetail] Error fetching order:', error);
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();

    if (!emailAddress) {
      toast.error('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailAddress)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSendingEmail(true);
    try {
      await axios.post(`${API}/orders/${orderId}/send-email`, {
        email: emailAddress
      });

      toast.success('Invoice sent successfully via email!');
      setIsSendEmailOpen(false);
      setEmailAddress('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendWhatsApp = async (e) => {
    e.preventDefault();

    if (!whatsappData.customerName.trim()) {
      toast.error('Please enter customer name');
      return;
    }

    if (!whatsappData.phoneNumber.trim()) {
      toast.error('Please enter phone number');
      return;
    }

    // Basic phone validation (10 digits or with country code)
    const phoneDigits = whatsappData.phoneNumber.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      toast.error('Please enter a valid phone number (at least 10 digits)');
      return;
    }

    setSendingWhatsApp(true);
    try {
      await axios.post(`${API}/orders/${orderId}/send-whatsapp`, {
        customer_name: whatsappData.customerName.trim(),
        phone_number: whatsappData.phoneNumber.trim()
      });

      toast.success('Invoice sent successfully via WhatsApp!');
      setIsSendWhatsAppOpen(false);
      setWhatsappData({ customerName: '', phoneNumber: '' });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send WhatsApp';
      toast.error(errorMsg);
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const handleShare = async () => {
    try {
      // Generate PDF
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Generate PDF content (same as download)
      doc.setFontSize(24);
      doc.setFont(undefined, 'bold');
      doc.text('1946 INVOICE', 14, 20);
      
      // Order Details Header
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      let yPos = 35;
      
      const orderDateTime = order.order_datetime || order.date_timestamp;
      const orderDate = orderDateTime ? new Date(orderDateTime) : new Date();
      
      // Left column
      doc.setFont(undefined, 'bold');
      doc.text('Order Details:', 14, yPos);
      doc.setFont(undefined, 'normal');
      yPos += 6;
      doc.text(`Order ID: ${order.order_no || order.order_id}`, 14, yPos);
      yPos += 5;
      doc.text(`Date: ${orderDate.toLocaleDateString()}`, 14, yPos);
      yPos += 5;
      doc.text(`Time: ${orderDate.toLocaleTimeString()}`, 14, yPos);
      yPos += 5;
      // Show Party Ledger OR Payment Method (not both)
      if (order.ledger_party_id || (order.ledger && order.ledger !== 'cash')) {
        doc.text(`Party Ledger: ${order.ledger_name || 'Ledger'}`, 14, yPos);
      } else {
        const pm = order.payment_method || 'cash';
        let paymentDisplay = pm === 'cash' ? 'Cash' : pm === 'digital' ? 'Digital' : pm === 'card' ? 'Card' : pm === 'other' ? (order.payment_method_other || 'Other') : 'Cash';
        doc.text(`Payment: ${paymentDisplay}`, 14, yPos);
      }
      
      // Right column - Franchise Details
      yPos = 35;
      doc.setFont(undefined, 'bold');
      doc.text('Franchise Details:', pageWidth / 2 + 10, yPos);
      doc.setFont(undefined, 'normal');
      yPos += 6;
      doc.text(`Name: ${order.franchise_name || user.franchise?.name || 'N/A'}`, pageWidth / 2 + 10, yPos);
      yPos += 5;
      if (user.franchise?.franchise_address) {
        doc.text(`Address: ${user.franchise.franchise_address}`, pageWidth / 2 + 10, yPos);
        yPos += 5;
      }
      if (user.franchise?.location) {
        doc.text(`Location: ${user.franchise.location}`, pageWidth / 2 + 10, yPos);
        yPos += 5;
      }
      if (user.franchise?.contact_number) {
        doc.text(`Contact: ${user.franchise.contact_number}`, pageWidth / 2 + 10, yPos);
      }
      
      // Line separator
      yPos = 70;
      doc.line(14, yPos, pageWidth - 14, yPos);
      
      // Items Table
      yPos += 10;
      doc.setFont(undefined, 'bold');
      doc.setFontSize(12);
      doc.text('Order Items', 14, yPos);
      
      // Table Headers
      yPos += 10;
      doc.setFontSize(10);
      doc.text('Item Name', 14, yPos);
      doc.text('Price', 100, yPos);
      doc.text('Qty', 130, yPos);
      doc.text('Total', 160, yPos, { align: 'right' });
      
      doc.line(14, yPos + 2, pageWidth - 14, yPos + 2);
      
      // Table Data
      yPos += 10;
      doc.setFont(undefined, 'normal');
      
      const items = JSON.parse(order.order_details_json || '{"items": []}')['items'];
      items.forEach((item) => {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.text(item.name || item.item_name_snapshot, 14, yPos);
        doc.text(`Rs ${parseFloat(item.price || item.item_price_snapshot).toFixed(2)}`, 100, yPos);
        doc.text(`${item.quantity}`, 130, yPos);
        doc.text(`Rs ${parseFloat(item.line_total || (item.quantity * (item.price || item.item_price_snapshot))).toFixed(2)}`, 160, yPos, { align: 'right' });
        yPos += 8;
      });
      
      // Line above total
      doc.line(14, yPos, pageWidth - 14, yPos);
      yPos += 8;
      
      // Grand Total
      doc.setFont(undefined, 'bold');
      doc.setFontSize(12);
      doc.text('Grand Total:', 14, yPos);
      doc.text(`Rs ${parseFloat(order.total_amount || order.total_value || 0).toFixed(2)}`, 160, yPos, { align: 'right' });
      
      // Footer
      yPos = doc.internal.pageSize.getHeight() - 30;
      doc.setFontSize(10);
      doc.setFont(undefined, 'italic');
      doc.text('Thank you for your business!', pageWidth / 2, yPos, { align: 'center' });
      yPos += 6;
      doc.setFontSize(8);
      doc.text('* This receipt is for your reference only', pageWidth / 2, yPos, { align: 'center' });

      // Get PDF as blob
      const pdfBlob = doc.output('blob');
      const pdfFile = new File([pdfBlob], `Invoice_${order.order_no}.pdf`, { type: 'application/pdf' });

      // Prepare share data
      const shareText = `Invoice from ${order.franchise_name}\n\nOrder: ${order.order_no}\nAmount: ₹${order.total_value}\nDate: ${orderDate.toLocaleDateString()}`;

      // Check if Web Share API with files is supported
      if (navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
        const shareData = {
          title: `Invoice #${order.order_no}`,
          text: shareText,
          files: [pdfFile]
        };

        try {
          await navigator.share(shareData);
          toast.success('Invoice PDF shared successfully!');
        } catch (error) {
          // User cancelled the share or error occurred
          if (error.name !== 'AbortError') {
            console.error('Error sharing:', error);
            // Fallback to download
            toast.info('Unable to share. Downloading PDF instead...');
            doc.save(`Invoice_${order.order_no}.pdf`);
          }
        }
      } else {
        // Fallback: Download PDF and copy text
        toast.info('File sharing not supported. Downloading PDF...');
        doc.save(`Invoice_${order.order_no}.pdf`);
        
        // Also copy share text to clipboard
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(shareText + `\n\nView invoice: ${window.location.href}`);
          toast.success('Invoice details copied to clipboard!');
        }
      }
    } catch (error) {
      console.error('Error generating/sharing PDF:', error);
      toast.error('Failed to share invoice. Please try download instead.');
    }
  };

  const handleCopyLink = () => {
    const invoiceLink = window.location.href;
    const shareText = `Invoice from ${order.franchise_name}\n\nOrder: ${order.order_no}\nAmount: ₹${order.total_value}\nDate: ${new Date(order.date_timestamp).toLocaleDateString()}\n\nView invoice: ${invoiceLink}`;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareText)
        .then(() => {
          toast.success('Invoice details copied to clipboard!');
        })
        .catch((err) => {
          console.error('Failed to copy:', err);
          toast.error('Failed to copy link. Please copy manually.');
        });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareText;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Invoice details copied to clipboard!');
      } catch (err) {
        toast.error('Failed to copy link. Please copy manually.');
      }
      document.body.removeChild(textArea);
    }
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header - Brand Name
    doc.setFontSize(22);
    doc.setFont(undefined, 'bold');
    doc.text('1946 - Filter Coffee & Tea', pageWidth / 2, 20, { align: 'center' });
    
    doc.line(14, 25, pageWidth - 14, 25);
    
    // Order Details Header
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    let yPos = 35;
    
    const orderDateTime = order.order_datetime || order.date_timestamp;
    const orderDate = orderDateTime ? new Date(orderDateTime) : new Date();
    
    // Left column
    doc.setFont(undefined, 'bold');
    doc.text('Order Details:', 14, yPos);
    doc.setFont(undefined, 'normal');
    yPos += 6;
    doc.text(`Order ID: ${order.order_no || order.order_id}`, 14, yPos);
    yPos += 5;
    doc.text(`Date: ${orderDate.toLocaleDateString()}`, 14, yPos);
    yPos += 5;
    doc.text(`Time: ${orderDate.toLocaleTimeString()}`, 14, yPos);
    yPos += 5;
    // Show Party Ledger OR Payment Method (not both)
    if (order.ledger_party_id || (order.ledger && order.ledger !== 'cash')) {
      doc.text(`Party Ledger: ${order.ledger_name || 'Ledger'}`, 14, yPos);
    } else {
      const pm2 = order.payment_method || 'cash';
      let paymentDisplay2 = pm2 === 'cash' ? 'Cash' : pm2 === 'digital' ? 'Digital' : pm2 === 'card' ? 'Card' : pm2 === 'other' ? (order.payment_method_other || 'Other') : 'Cash';
      doc.text(`Payment: ${paymentDisplay2}`, 14, yPos);
    }
    
    // Right column - Franchise Details
    yPos = 35;
    doc.setFont(undefined, 'bold');
    doc.text('Franchise Details:', pageWidth / 2 + 10, yPos);
    doc.setFont(undefined, 'normal');
    yPos += 6;
    doc.text(`Name: ${order.franchise_name || user.franchise?.name || 'N/A'}`, pageWidth / 2 + 10, yPos);
    yPos += 5;
    if (user.franchise?.franchise_address) {
      doc.text(`Address: ${user.franchise.franchise_address}`, pageWidth / 2 + 10, yPos);
      yPos += 5;
    }
    if (user.franchise?.location) {
      doc.text(`Location: ${user.franchise.location}`, pageWidth / 2 + 10, yPos);
      yPos += 5;
    }
    if (user.franchise?.contact_number) {
      doc.text(`Contact: ${user.franchise.contact_number}`, pageWidth / 2 + 10, yPos);
    }
    
    // Line separator
    yPos = 70;
    doc.line(14, yPos, pageWidth - 14, yPos);
    
    // Items Table
    yPos += 10;
    doc.setFont(undefined, 'bold');
    doc.setFontSize(12);
    doc.text('Order Items', 14, yPos);
    
    // Table Headers
    yPos += 10;
    doc.setFontSize(10);
    doc.text('Item Name', 14, yPos);
    doc.text('Price', 100, yPos);
    doc.text('Qty', 130, yPos);
    doc.text('Total', 160, yPos, { align: 'right' });
    
    doc.line(14, yPos + 2, pageWidth - 14, yPos + 2);
    
    // Table Data
    yPos += 10;
    doc.setFont(undefined, 'normal');
    
    items.forEach((item) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.text(item.name || item.item_name_snapshot, 14, yPos);
      doc.text(`Rs ${parseFloat(item.price || item.item_price_snapshot).toFixed(2)}`, 100, yPos);
      doc.text(`${item.quantity}`, 130, yPos);
      doc.text(`Rs ${parseFloat(item.line_total || (item.quantity * (item.price || item.item_price_snapshot))).toFixed(2)}`, 160, yPos, { align: 'right' });
      yPos += 8;
    });
    
    // Line above total
    doc.line(14, yPos, pageWidth - 14, yPos);
    yPos += 8;
    
    // Grand Total
    doc.setFont(undefined, 'bold');
    doc.setFontSize(12);
    doc.text('Grand Total:', 14, yPos);
    doc.text(`Rs ${parseFloat(order.total_amount || order.total_value || 0).toFixed(2)}`, 160, yPos, { align: 'right' });
    
    // Footer
    yPos = doc.internal.pageSize.getHeight() - 30;
    doc.setFontSize(10);
    doc.setFont(undefined, 'italic');
    doc.text('Thank you for your business!', pageWidth / 2, yPos, { align: 'center' });
    yPos += 6;
    doc.setFontSize(8);
    doc.text('* This receipt is for your reference only', pageWidth / 2, yPos, { align: 'center' });
    
    // Save PDF
    doc.save(`Order_${order.order_no || order.order_id}.pdf`);
    toast.success('Invoice downloaded successfully!');
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!order) {
    return <div className="flex items-center justify-center min-h-screen">Order not found</div>;
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="max-w-4xl mx-auto p-3 sm:p-6">
        {/* Top Navigation */}
        <div className="mb-4 sm:mb-6 flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="h-10 px-2 sm:px-4"
          >
            <ArrowLeft className="mr-1 sm:mr-2 h-4 w-4" />
            <span className="text-sm sm:text-base">Back</span>
          </Button>
          <Button
            onClick={() => navigate('/generate-order')}
            className="h-10 sm:h-12 bg-primary text-white hover:bg-primary/90 rounded-full px-3 sm:px-6 font-bold text-xs sm:text-sm"
          >
            <span className="hidden xs:inline">Generate</span> New Sale
          </Button>
        </div>

        <Card className="shadow-lg border-border">
          <CardHeader className={`p-4 sm:p-6 ${order.is_deleted ? 'bg-red-50' : 'border-b border-border'}`}>
            {/* Header - Stack on mobile, row on larger screens */}
            <div className="flex flex-col gap-4">
              {/* Logo and Title Row */}
              <div className="flex items-center gap-3 sm:gap-4">
                <img 
                  src={clientLogo || "/fos-logo.png"} 
                  alt={clientLogo ? "Business Logo" : "FOS Logo"} 
                  className="h-10 sm:h-14 w-auto object-contain flex-shrink-0 rounded"
                  onError={(e) => { e.target.src = '/fos-logo.png'; }}
                />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <CardTitle className="text-xl sm:text-2xl md:text-3xl font-bold text-primary">Invoice</CardTitle>
                    {order.is_deleted && (
                      <span className="px-2 py-0.5 sm:px-3 sm:py-1 bg-red-600 text-white text-xs sm:text-sm font-bold rounded-full whitespace-nowrap">
                        DELETED
                      </span>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-secondary mt-1">{user?.business_name || 'FOS Billing System'}</p>
                </div>
              </div>
              
              {/* Action Buttons - Full width on mobile */}
              <div className="flex flex-wrap gap-2 sm:gap-3">
                <Button
                  onClick={() => setIsSendWhatsAppOpen(true)}
                  variant="outline"
                  className="flex-1 sm:flex-none h-10 sm:h-11 border-2 border-green-500 text-green-600 hover:bg-green-500 hover:text-white rounded-full px-3 sm:px-5 font-semibold flex items-center justify-center gap-1.5 sm:gap-2 text-sm"
                  title="Send via WhatsApp"
                >
                  <MessageCircle className="h-4 w-4" />
                  <span>WhatsApp</span>
                </Button>
                <Button
                  onClick={handleShare}
                  variant="outline"
                  className="flex-1 sm:flex-none h-10 sm:h-11 border-2 border-blue-500 text-blue-600 hover:bg-blue-500 hover:text-white rounded-full px-3 sm:px-5 font-semibold flex items-center justify-center gap-1.5 sm:gap-2 text-sm"
                  title="Share invoice"
                >
                  <Share2 className="h-4 w-4" />
                  <span>Share</span>
                </Button>
                <Button
                  onClick={() => setIsSendEmailOpen(true)}
                  variant="outline"
                  className="flex-1 sm:flex-none h-10 sm:h-11 border-2 border-accent text-accent hover:bg-accent hover:text-white rounded-full px-3 sm:px-5 font-semibold flex items-center justify-center gap-1.5 sm:gap-2 text-sm"
                >
                  <Send className="h-4 w-4" />
                  <span>Email</span>
                </Button>
                <Button
                  data-testid="download-pdf-button"
                  onClick={handleDownloadPDF}
                  className="flex-1 sm:flex-none h-10 sm:h-11 bg-accent text-white hover:bg-accent/90 rounded-full px-3 sm:px-5 font-semibold flex items-center justify-center gap-1.5 sm:gap-2 text-sm"
                >
                  <Download className="h-4 w-4" />
                  <span>Download</span>
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-3 sm:p-6">
            {/* Deletion Notice */}
            {order.is_deleted && (
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-100 border-2 border-red-400 rounded-lg">
                <h3 className="text-base sm:text-lg font-bold text-red-800 mb-2">⚠️ This Order Has Been Deleted</h3>
                <div className="space-y-1 text-xs sm:text-sm text-red-900">
                  <p><span className="font-semibold">Deleted At:</span> {new Date(order.deleted_at).toLocaleString()}</p>
                  <p><span className="font-semibold">Reason:</span> {order.deleted_reason}</p>
                  <p className="text-xs text-red-700 mt-2">
                    ℹ️ This order is kept for audit purposes and is excluded from all reports and totals.
                  </p>
                </div>
              </div>
            )}

            {/* Order and Franchise Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-8 p-3 sm:p-6 bg-muted rounded-xl">
              <div>
                <h3 className="text-base sm:text-lg font-bold text-primary mb-3 sm:mb-4">Order Details</h3>
                <div className="space-y-2">
                  <div className="text-xs sm:text-sm">
                    <span className="font-medium text-secondary">Order ID:</span>
                    <span className="font-bold text-primary block sm:inline sm:ml-1 break-all">{order.order_no || order.order_id}</span>
                  </div>
                  <p className="text-xs sm:text-sm"><span className="font-medium text-secondary">Date:</span> <span className="ml-1">{formatDateTimeIST(order.order_datetime || order.date_timestamp || order.created_at, 'date')}</span></p>
                  <p className="text-xs sm:text-sm"><span className="font-medium text-secondary">Time:</span> <span className="ml-1">{formatDateTimeIST(order.order_datetime || order.date_timestamp || order.created_at, 'time')}</span></p>
                  {/* Show Party Ledger OR Payment Method (not both) */}
                  {(order.ledger_party_id || (order.ledger && order.ledger !== 'cash')) ? (
                    <p className="text-xs sm:text-sm"><span className="font-medium text-secondary">Party Ledger:</span> <span className="font-bold text-amber-600 ml-1">{order.ledger_name || 'Ledger'}</span></p>
                  ) : (
                    <p className="text-xs sm:text-sm"><span className="font-medium text-secondary">Payment:</span> <span className="font-bold text-primary ml-1">
                      {(() => {
                        const pm = order.payment_method || 'cash';
                        let display = pm === 'cash' ? 'Cash' : pm === 'digital' ? 'Digital' : pm === 'card' ? 'Card' : pm === 'other' ? (order.payment_method_other || 'Other') : 'Cash';
                        return display;
                      })()}
                    </span></p>
                  )}
                  <p className="text-xs sm:text-sm">
                    <span className="font-medium text-secondary">Status:</span> 
                    <span className={`font-bold ml-1 sm:ml-2 ${order.is_deleted ? 'text-red-600' : 'text-green-600'}`}>
                      {order.is_deleted ? 'DELETED' : (order.status || 'COMPLETED')}
                    </span>
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-base sm:text-lg font-bold text-primary mb-3 sm:mb-4">Franchise Details</h3>
                <div className="space-y-2">
                  <p className="text-xs sm:text-sm"><span className="font-medium text-secondary">Name:</span> <span className="font-bold text-primary ml-1">{order.franchise_name || user.franchise?.name || 'N/A'}</span></p>
                  {(user.franchise?.franchise_address) && (
                    <p className="text-xs sm:text-sm"><span className="font-medium text-secondary">Address:</span> <span className="ml-1">{user.franchise.franchise_address}</span></p>
                  )}
                  <div className="text-xs sm:text-sm">
                    <span className="font-medium text-secondary">Franchise ID:</span>
                    <span className="block sm:inline sm:ml-1 break-all">{order.franchise_id || user.franchise?.id || 'N/A'}</span>
                  </div>
                  {user.franchise?.location && (
                    <p className="text-xs sm:text-sm"><span className="font-medium text-secondary">Location:</span> <span className="ml-1">{user.franchise.location}</span></p>
                  )}
                  {user.franchise?.contact_number && (
                    <p className="text-xs sm:text-sm"><span className="font-medium text-secondary">Contact:</span> <span className="ml-1">{user.franchise.contact_number}</span></p>
                  )}
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-base sm:text-lg font-bold text-primary mb-2">Payment Information</h3>
              {/* Show Party Ledger OR Payment Method (not both) */}
              {(order.ledger_party_id || (order.ledger && order.ledger !== 'cash')) ? (
                <>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <span className="text-xs sm:text-sm font-medium text-secondary">Party Ledger:</span>
                    <span className="text-sm sm:text-base font-bold text-amber-600">
                      {order.ledger_name || order.ledger}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-amber-700 mt-1">
                    Debited to Party Ledger
                  </p>
                </>
              ) : (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <span className="text-xs sm:text-sm font-medium text-secondary">Payment Method:</span>
                  <span className="text-sm sm:text-base font-bold text-accent">
                    {(() => {
                      const pm = order.payment_method || 'cash';
                      let display = pm === 'cash' ? 'Cash' : pm === 'digital' ? 'Digital (UPI/Online)' : pm === 'card' ? 'Card' : pm === 'other' ? (order.payment_method_other || 'Other') : 'Cash';
                      return display;
                    })()}
                  </span>
                </div>
              )}
            </div>

            {/* Order Items */}
            <div className="mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-primary mb-3 sm:mb-4">Order Items</h3>
              <div className="overflow-x-auto -mx-3 sm:mx-0">
                <table className="w-full min-w-[320px]">
                  <thead>
                    <tr className="border-b-2 border-primary">
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-3 font-bold text-primary text-xs sm:text-sm">Item</th>
                      <th className="text-right py-2 sm:py-3 px-1 sm:px-2 font-bold text-primary text-xs sm:text-sm">Price</th>
                      <th className="text-center py-2 sm:py-3 px-1 sm:px-2 font-bold text-primary text-xs sm:text-sm">Qty</th>
                      <th className="text-right py-2 sm:py-3 px-2 sm:px-3 font-bold text-primary text-xs sm:text-sm">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items && items.length > 0 ? (
                      items.map((item, index) => (
                        <tr key={index} className="border-b border-border hover:bg-muted/50">
                          <td className="py-3 sm:py-4 px-2 sm:px-3 text-primary font-medium text-xs sm:text-sm">{item.name || item.item_name_snapshot}</td>
                          <td className="py-3 sm:py-4 px-1 sm:px-2 text-right text-secondary text-xs sm:text-sm">₹{parseFloat(item.price || item.item_price_snapshot || 0).toFixed(2)}</td>
                          <td className="py-3 sm:py-4 px-1 sm:px-2 text-center text-secondary text-xs sm:text-sm">{item.quantity || 0}</td>
                          <td className="py-3 sm:py-4 px-2 sm:px-3 text-right font-bold text-accent text-xs sm:text-sm">
                            ₹{parseFloat(item.line_total || (item.quantity * (item.price || item.item_price_snapshot)) || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="py-6 sm:py-8 text-center text-secondary text-sm">No items found</td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-primary">
                      <td colSpan="3" className="py-3 sm:py-4 px-2 text-right font-bold text-base sm:text-xl text-primary">Grand Total:</td>
                      <td className="py-3 sm:py-4 px-2 sm:px-3 text-right font-bold text-xl sm:text-3xl text-accent">
                        ₹{parseFloat(order.total_amount || order.total_value || 0).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Footer Message */}
            <div className="text-center mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-border">
              <p className="text-base sm:text-lg font-medium text-primary mb-2">Thank you for your business!</p>
              <p className="text-xs sm:text-sm text-secondary italic">* This receipt is for your reference only</p>
            </div>
          </CardContent>
        </Card>

        {/* Send Email Modal */}
        <Dialog open={isSendEmailOpen} onOpenChange={setIsSendEmailOpen}>
          <DialogContent className="bg-surface max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-primary flex items-center gap-2">
                <Send className="h-5 w-5 text-accent" />
                Send Invoice via Email
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSendEmail} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="email-address">Customer Email Address</Label>
                <Input
                  id="email-address"
                  type="email"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  placeholder="customer@example.com"
                  className="h-12 mt-2"
                  autoFocus
                  required
                />
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  📧 The invoice will be sent as a professional email with PDF attachment.
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsSendEmailOpen(false);
                    setEmailAddress('');
                  }}
                  className="flex-1 h-11"
                  disabled={sendingEmail}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-11 bg-accent text-white hover:bg-accent/90"
                  disabled={sendingEmail}
                >
                  {sendingEmail ? 'Sending...' : 'Send Email'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Send WhatsApp Modal */}
        <Dialog open={isSendWhatsAppOpen} onOpenChange={setIsSendWhatsAppOpen}>
          <DialogContent className="bg-surface max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-primary flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-green-500" />
                Send Invoice via WhatsApp
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSendWhatsApp} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="customer-name">Customer Name</Label>
                <Input
                  id="customer-name"
                  type="text"
                  value={whatsappData.customerName}
                  onChange={(e) => setWhatsappData(prev => ({ ...prev, customerName: e.target.value }))}
                  placeholder="Enter customer name"
                  className="h-12 mt-2"
                  autoFocus
                  required
                />
              </div>

              <div>
                <Label htmlFor="phone-number">WhatsApp Number</Label>
                <Input
                  id="phone-number"
                  type="tel"
                  value={whatsappData.phoneNumber}
                  onChange={(e) => setWhatsappData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                  placeholder="9876543210 or +919876543210"
                  className="h-12 mt-2"
                  required
                />
                <p className="text-xs text-secondary mt-1">Enter 10-digit number or with country code</p>
              </div>

              {/* Order Preview */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-2">
                <p className="text-sm font-semibold text-green-800 flex items-center gap-2">
                  <MessageCircle className="h-4 w-4" />
                  Message Preview
                </p>
                <div className="text-xs text-green-700 space-y-1">
                  <p>Hello {whatsappData.customerName || '[Customer Name]'}! 👋</p>
                  <p>Greetings from <strong>1946 Filter Coffee and Tea</strong></p>
                  <p>Thank you for visiting <strong>{order?.franchise_name || 'our outlet'}</strong>!</p>
                  <p className="mt-2">📋 <strong>Order Summary</strong></p>
                  <p>Order No: {order?.order_no}</p>
                  <p>Amount: ₹{parseFloat(order?.total_value || 0).toFixed(2)}</p>
                  <p className="mt-2 italic">+ Invoice PDF attached</p>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsSendWhatsAppOpen(false);
                    setWhatsappData({ customerName: '', phoneNumber: '' });
                  }}
                  className="flex-1 h-11"
                  disabled={sendingWhatsApp}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-11 bg-green-500 text-white hover:bg-green-600"
                  disabled={sendingWhatsApp}
                >
                  {sendingWhatsApp ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <MessageCircle className="h-4 w-4" />
                      Send WhatsApp
                    </span>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* FOS Watermark */}
      <FOSWatermark opacity={0.06} size="md" />
    </div>
  );
};

export default OrderDetail;
